import { ref, type Ref } from 'vue'
import type { LovOption } from './types'

/** LOV 缓存写入时递增，供 Grid 等同步读缓存的展示层建立响应式依赖 */
const lovCacheVersion = ref(0)

export function useLovCacheVersion(): Ref<number> {
  return lovCacheVersion
}

function bumpLovCacheVersion(): void {
  lovCacheVersion.value += 1
}

interface CacheEntry {
  expireAt: number
  data: LovOption[]
}

const memoryCache = new Map<string, CacheEntry>()
const pending = new Map<string, Promise<LovOption[]>>()

export function buildLovCacheKey(
  lovType: string,
  params?: Record<string, unknown>
): string {
  return `${lovType}::${JSON.stringify(params ?? {})}`
}

export function getLovFromCache(
  key: string,
  ttl: number
): LovOption[] | undefined {
  if (ttl <= 0) return undefined
  const entry = memoryCache.get(key)
  if (!entry) return undefined
  if (Date.now() > entry.expireAt) {
    memoryCache.delete(key)
    return undefined
  }
  return entry.data
}

export function setLovCache(
  key: string,
  data: LovOption[],
  ttl: number
): void {
  if (ttl <= 0) return
  memoryCache.set(key, {
    data,
    expireAt: Date.now() + ttl,
  })
  bumpLovCacheVersion()
}

export function getPendingLov(key: string): Promise<LovOption[]> | undefined {
  return pending.get(key)
}

export function setPendingLov(
  key: string,
  promise: Promise<LovOption[]>
): void {
  pending.set(key, promise)
}

export function clearPendingLov(key: string): void {
  pending.delete(key)
}

export function clearLovCache(): void {
  memoryCache.clear()
  pending.clear()
  bumpLovCacheVersion()
}

const DEFAULT_LABEL_TTL = 5 * 60 * 1000

function buildLovLookupKeys(value: unknown): string[] {
  if (value == null || value === '') {
    return []
  }
  const keys = new Set<string>()
  keys.add(String(value))
  if (typeof value === 'number' && !Number.isNaN(value)) {
    keys.add(String(Math.trunc(value)))
  }
  return [...keys]
}

function findLovOption(
  lovType: string | undefined,
  value: unknown,
  ttl: number = DEFAULT_LABEL_TTL
): LovOption | undefined {
  if (!lovType) {
    return undefined
  }
  const cached = getLovFromCache(buildLovCacheKey(lovType), ttl)
  if (!cached?.length) {
    return undefined
  }
  for (const key of buildLovLookupKeys(value)) {
    const found = cached.find((item) => item.value === key)
    if (found) {
      return found
    }
  }
  return undefined
}

/** 从缓存读取展示文案（同步，供 Table 单元格渲染） */
export function getLovLabel(
  lovType: string | undefined,
  value: unknown,
  ttl: number = DEFAULT_LABEL_TTL
): string {
  return findLovOption(lovType, value, ttl)?.label ?? ''
}

/** 从缓存读取 Tag 类型（同步，供 LOV 列 z-tag 展示） */
export function getLovTagType(
  lovType: string | undefined,
  value: unknown,
  ttl: number = DEFAULT_LABEL_TTL
) {
  return findLovOption(lovType, value, ttl)?.tagType
}
